"use client";

import { CheckIcon } from "@heroicons/react/20/solid";

import { PricingButton } from "@/components/pricing/pricingButton/PricingButton";
import { Badge } from "@/components/text/Badge";
import { NormalText } from "@/components/text/NormalText";
import { OverHeading } from "@/components/text/OverHeading";
import { SoftText } from "@/components/text/SoftText";
import { PricePlan, UserNoDates } from "@/types/types";
import { cn } from "@/utils/cn";

export function PricingCard({
  pricePlan,
  user,
  monthly,
  numberOfModels,
  closeModal,
}: {
  pricePlan: PricePlan;
  user: UserNoDates | null;
  monthly: boolean;
  numberOfModels: number;
  closeModal: (() => void) | null;
}) {
  const getMonthlyPrice = (): number | null => {
    if (pricePlan.prices === null) {
      return null;
    }

    if (monthly) {
      return pricePlan.prices.monthly.priceAmountPerMonth;
    }

    return pricePlan.prices.yearly.priceAmountPerMonth;
  };

  const getStripeId = (): string | null => {
    if (pricePlan.prices === null) {
      return null;
    }

    if (monthly) {
      return pricePlan.prices.monthly.stripeId;
    }

    return pricePlan.prices.yearly.stripeId;
  };

  const monthlyPrice = getMonthlyPrice();
  const stripeId = getStripeId();

  const getBillingText = (): string => {
    if (monthlyPrice === null) {
      return "Free";
    }

    return `$${monthlyPrice * 12} billed yearly`;
  };

  const isEmphasizedFeature = (index: number): boolean => {
    return index === 0 || index === 1 || index === 2;
  };

  const getPlanNameId = (): string => {
    return pricePlan.name;
  };

  return (
    <div className="relative flex justify-center sm:px-4">
      <div className="w-full max-w-xs">
        <div className="flex gap-x-6">
          <OverHeading
            id={getPlanNameId()}
            className="text-gray-900"
            elementType="h2"
          >
            {pricePlan.name}
          </OverHeading>
          {pricePlan.highlighted && <Badge>Recommended</Badge>}
        </div>

        <p className="mt-2 flex items-baseline gap-x-1 lg:mt-6">
          <span className="text-5xl font-bold tracking-tight">
            {monthlyPrice === null ? "Free" : `$${monthlyPrice}`}
          </span>

          {monthlyPrice !== null && (
            <SoftText elementType="span">/month</SoftText>
          )}
        </p>

        {!monthly && (
          <div className="mt-2">
            <Badge
              className={cn(
                "mt-2 text-gray-900",
                monthlyPrice === null ? "invisible" : ""
              )}
            >
              {getBillingText()}
            </Badge>
          </div>
        )}

        <PricingButton
          user={user}
          stripeSubscriptionId={stripeId}
          numberOfModels={numberOfModels}
          closeModal={closeModal}
        />

        <NormalText elementType="p" className="mt-6 lg:mt-10">
          {pricePlan.description}
        </NormalText>

        <ul
          role="list"
          className="mt-4 space-y-3 text-sm leading-6 text-gray-600 lg:mt-6"
        >
          {pricePlan.features.map((feature, index) => (
            <li key={feature} className="flex gap-x-2">
              <CheckIcon
                className={cn(
                  monthlyPrice === null ? "text-gray-400" : "text-primary",
                  "h-5 w-5 flex-none"
                )}
                aria-hidden="true"
              />
              <NormalText
                elementType="span"
                className={cn(
                  isEmphasizedFeature(index) ? "font-semibold" : "",
                  "text-sm"
                )}
              >
                {feature}
              </NormalText>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
